/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {ApiConfig} from '../../../components/api/api';
import {Downloader} from '../../../components/util/downloader';
import {formatCsvOutput} from '../filters/csv-output';
import {AdnHttpService} from '../../../components/util/adn-http.service';
import {
  AdnHttpSender,
  createAdnHttpResourceSender
} from '../../../components/util/create-adn-http-resource';
export interface AngularResourceListExportConfig {
  baseTitle: string;
  columns: string[];
  headingFields: string[];
  mapItem?: (item: any) => any;
}

export interface AngularResourceListConfig {
  resourcePath: string;
  listId: string;
  defaultOrderByParam?: string;
  concise?: boolean;
  includePageParam?: boolean;
  queryParams?: (list: any) => Record<string, any>;
  exportConfig?: AngularResourceListExportConfig;
  afterLoad?: (page: any, list: any) => void;
}

export interface AngularResourceListDependencies {
  AdnHttp: AdnHttpService;
  authHttpInterceptor: any;
  ApiErrorHandler: any;
  window: any;
  localUserProfile: any;
  ngZone: any;
}

export class AngularResourceListManager {
  readonly list: any;

  private readonly uri: string;
  private readonly send: AdnHttpSender;
  private readonly onPopState = () => {
    if (!this.list) {
      return;
    }
    const currentPage = this.getCurrentPage();
    if (currentPage === this.list.currentPage) {
      return;
    }
    this.list.currentPage = currentPage;
    this.load('pageQuery');
  };

  constructor(
    private readonly deps: AngularResourceListDependencies,
    private readonly config: AngularResourceListConfig
  ) {
    this.uri = ApiConfig.obtain().getVersionedUri(config.resourcePath);
    this.send = createAdnHttpResourceSender(
      deps.AdnHttp,
      deps.authHttpInterceptor,
      deps.ApiErrorHandler
    );
    this.list = this.buildList();

    if (this.config.includePageParam !== false) {
      this.deps.window.addEventListener('popstate', this.onPopState);
    }
  }

  load(queryKey: 'query' | 'sortQuery' | 'pageQuery' | 'includeQuery' = 'query'): Promise<void> {
    this.list[queryKey] = {$resolved: false};
    if (queryKey === 'query') {
      this.list.query = {$resolved: false};
    }

    return this.send({
      method: 'GET',
      url: this.uri,
      params: _.assign({
        orderBy: this.list.orderByParam,
        concise: this.config.concise !== false,
        orderByDirection: this.list.asc ? 'ASCENDING' : 'DESCENDING',
        nullsOrderPolicy: this.list.nullsOrderPolicy,
        pageSize: this.list.pageSize,
        page: this.list.currentPage,
        locale: this.deps.localUserProfile.get('locale'),
        includeActive: this.list.include !== 'INACTIVE' && this.list.include !== 'HIDDEN',
        includeInactive: this.list.include === 'INACTIVE',
        includeHidden: this.list.include === 'HIDDEN'
      }, this.config.queryParams ? this.config.queryParams(this.list) : {})
    }).then((page: any) => {
      this.deps.ngZone.run(() => {
        this.list.items = page.results;
        this.list.totalCount = page.totalCount;
        this.list.showingInclude = this.list.include;
        if (_.isFunction(this.config.afterLoad)) {
          this.config.afterLoad(page, this.list);
        }
        if (this.list[queryKey]) {
          this.list[queryKey].$resolved = true;
        }
        if (this.list.query) {
          this.list.query.$resolved = true;
        }
      });
    }, () => {
      this.deps.ngZone.run(() => {
        this.list.items = [];
        this.list.totalCount = 0;
        if (this.list[queryKey]) {
          this.list[queryKey].$resolved = true;
        }
        if (this.list.query) {
          this.list.query.$resolved = true;
        }
      });
    });
  }

  destroy(): void {
    if (this.config.includePageParam !== false) {
      this.deps.window.removeEventListener('popstate', this.onPopState);
    }
  }

  private buildList(): any {
    const profile = this.deps.localUserProfile.getProfile() || {};
    const listSettings = _.get(profile, ['adnLists', this.config.listId], {});
    const defaultPageSize = _.get(profile, ['adnPrefs', 'defaultPageSize']) || 25;
    const defaultSort = this.getDefaultSort();
    const asc = _.isBoolean(listSettings.asc) ? listSettings.asc : defaultSort.asc;
    const list: any = {
      items: [],
      totalCount: 0,
      currentPage: this.getCurrentPage(),
      pageSize: Math.max(1, Math.min(100, defaultPageSize)),
      orderByParam: listSettings.orderByParam || defaultSort.orderByParam,
      asc,
      nullsOrderPolicy: listSettings.isNumeric ? (asc ? 'FIRST' : 'LAST') : undefined,
      include: 'ACTIVE',
      showingInclude: 'ACTIVE',
      query: {$resolved: false},
      sortQuery: {$resolved: true},
      pageQuery: {$resolved: true},
      includeQuery: {$resolved: true},
      changePage: () => {
        this.writeCurrentPage(list.currentPage);
        this.load('pageQuery');
      },
      makeSortQuery: (param: string, isNumeric?: boolean) => {
        const isAsc = param === list.orderByParam ? !list.asc : !isNumeric;
        list.orderByParam = param;
        list.asc = isAsc;
        list.nullsOrderPolicy = isNumeric ? (isAsc ? 'FIRST' : 'LAST') : undefined;
        this.saveListSettings({
          orderByParam: param,
          asc: isAsc,
          isNumeric: !!isNumeric
        });
        this.load('sortQuery');
      },
      makeIncludeQuery: () => {
        list.currentPage = 1;
        this.load('includeQuery');
      }
    };

    if (this.config.exportConfig) {
      list.csvTableTitle = this.config.exportConfig.baseTitle + '.csv';
      list.headingFields = this.config.exportConfig.headingFields;
      list.columns = this.config.exportConfig.columns;
      list.getRows = () => this.getRows();
      list.saveCsv = () => this.saveCsv();
      list.saveXls = () => this.saveXls();
    }

    return list;
  }

  private getRows(): any[] {
    if (!this.config.exportConfig) {
      return [];
    }

    return _.map(_.cloneDeep(this.list.items), (item) => {
      const mappedItem = this.config.exportConfig?.mapItem ? this.config.exportConfig.mapItem(item) : item;
      _.forEach(this.config.exportConfig?.columns || [], (column) => {
        mappedItem[column] = formatCsvOutput(mappedItem[column], column);
      });
      return mappedItem;
    });
  }

  private saveXls(): void {
    if (!this.config.exportConfig) {
      return;
    }

    new Downloader().saveXls2(
      this.config.exportConfig.baseTitle + '.xls',
      this.config.exportConfig.headingFields,
      this.config.exportConfig.columns,
      this.getRows(),
      []
    );
  }

  private saveCsv(): void {
    if (!this.config.exportConfig) {
      return;
    }

    const rows = this.getRows();
    const header = (this.config.exportConfig.headingFields || []).map((value) => this.escapeCsvValue(value)).join(',');
    const body = rows.map((row) => {
      return (this.config.exportConfig?.columns || []).map((column) => {
        return this.escapeCsvValue(row[column]);
      }).join(',');
    }).join('\r\n');
    const csv = '\uFEFF' + header + '\r\n' + body;
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = this.deps.window.URL.createObjectURL(blob);
    const link = this.deps.window.document.createElement('a');
    link.href = url;
    link.download = this.list.csvTableTitle;
    this.deps.window.document.body.appendChild(link);
    link.click();
    this.deps.window.document.body.removeChild(link);
    this.deps.window.URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: any): string {
    if (_.isNil(value)) {
      return '';
    }

    const stringValue = _.isDate(value) ? value.toISOString() : String(value);
    if (/[",\r\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  private getCurrentPage(): number {
    const pageNo = parseInt(new URLSearchParams(this.deps.window.location.search).get('pageNo') || '', 10);
    return Number.isFinite(pageNo) && pageNo > 0 ? pageNo : 1;
  }

  private writeCurrentPage(page: number): void {
    if (this.config.includePageParam === false) {
      return;
    }

    const url = new URL(this.deps.window.location.href);
    if (page > 1) {
      url.searchParams.set('pageNo', String(page));
    } else {
      url.searchParams.delete('pageNo');
    }
    this.deps.window.history.replaceState(this.deps.window.history.state, '', url.toString());
  }

  private saveListSettings(settings: any): void {
    const profile = _.cloneDeep(this.deps.localUserProfile.getProfile() || {});
    _.set(
      profile,
      ['adnLists', this.config.listId],
      _.assign({}, _.get(profile, ['adnLists', this.config.listId], {}), settings)
    );
    this.deps.localUserProfile.saveProfile(profile);
  }

  private getDefaultSort(): {orderByParam: string; asc: boolean} {
    const defaultOrderByParam = this.config.defaultOrderByParam || 'name';
    if (_.startsWith(defaultOrderByParam, 'DESC')) {
      return {
        orderByParam: defaultOrderByParam.replace(/^DESC/, ''),
        asc: false
      };
    }

    return {
      orderByParam: defaultOrderByParam,
      asc: true
    };
  }
}
